/* Owns the session gate of PANEL.md §2: bootstraps `GET /v1/auth/me` behind the
   refresh cookie, holds the tree back while that is in flight, and redirects an
   anonymous visitor to `/login?next=<current path>`. It also installs the
   navigate-based session-lost handler so an unrecoverable 401 deep inside the
   app does not full-reload the page. */

import { useQuery } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'

import { Skeleton } from '../components/ui/Skeleton.js'
import { useT } from '../i18n/index.js'
import { setSessionLostHandler } from '../lib/api.js'
import { markAnonymous, useAuthState } from '../lib/auth-store.js'
import { meQueryOptions } from './api.js'

export interface RequireAuthProps {
  children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps): ReactNode {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const { status } = useAuthState()
  const session = useQuery(meQueryOptions())

  useEffect(() => {
    // `lib/api.ts` boots with a `location.assign` fallback because it has no
    // router; once the tree is mounted a client-side navigation is both faster
    // and keeps the `next=` round trip inside the SPA.
    setSessionLostHandler(() => {
      const next = `${window.location.pathname}${window.location.search}`
      void navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true })
    })
  }, [navigate])

  useEffect(() => {
    // A rejected bootstrap is the definition of "anonymous"; recording it in the
    // store stops every other subscriber from waiting on a session that will
    // never arrive.
    if (session.isError) markAnonymous()
  }, [session.isError])

  if (session.isPending && status === 'unknown') {
    return (
      <div className="flex min-h-screen flex-col gap-4 bg-surface p-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t('common.loading')}</span>
        <Skeleton height="2.5rem" rounded="md" />
        <Skeleton height="14rem" rounded="lg" />
        <Skeleton height="14rem" rounded="lg" />
      </div>
    )
  }

  if (session.isError || status === 'anonymous') {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  return <>{children}</>
}
